import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useCall } from './CallProvider';
import {
  BOARD_WIDTHS,
  decodeBoardMsg,
  encodeBoardMsg,
  type Pt,
} from './boardMath';
import { recognize, shapePoints } from './shapes';

/*
 * Пометки поверх демонстрации экрана (решение Ильи 16.09.2026).
 *
 * От доски отличается одним: у этих штрихов нет хранимого слоя. Они живут
 * только в комнате, идут тем же каналом данных, что и живой слой доски, и
 * никуда не записываются — ни на сервер, ни в итоговый файл. Показ кончился,
 * пометки исчезли вместе с ним.
 *
 * Отсюда и «Удалить всё» вместо ластика с отменой: держать историю нечем, да
 * и пометки поверх чужого экрана живут минуту-другую, пока о них говорят.
 */

/** Штрих поверх экрана: то же, что у доски, но без номера в базе. */
type OverStroke = { nonce: string; author: string; color: string; width: number; pts: Pt[] };

const newNonce = () => Math.random().toString(36).slice(2);

export function useScreenDraw({ enabled }: { enabled: boolean }) {
  const call = useCall();
  const { sendBoardData, onBoardData, me } = call;
  const [strokes, setStrokes] = useState<OverStroke[]>([]);
  const [on, setOn] = useState(false);

  // Показ кончился — пометок больше нет: они привязаны к тому, что показывали.
  useEffect(() => {
    if (!enabled) {
      setStrokes([]);
      setOn(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    return onBoardData((bytes, from) => {
      const msg = decodeBoardMsg(bytes);
      if (!msg) return;
      // Чужие сообщения доски сюда не относятся: берём только помеченные.
      if (msg.t === 'stroke' && msg.over) {
        if (from === me) return; // своё уже нарисовано
        setStrokes((s) => [...s, { nonce: msg.nonce, author: from, color: msg.color, width: msg.width, pts: msg.pts }]);
        return;
      }
      if (msg.t === 'clear') setStrokes([]);
    });
  }, [enabled, me, onBoardData]);

  const add = useCallback(
    (stroke: OverStroke) => {
      setStrokes((s) => [...s, stroke]);
      sendBoardData(
        encodeBoardMsg({
          t: 'stroke',
          nonce: stroke.nonce,
          author: stroke.author,
          color: stroke.color,
          width: stroke.width,
          pts: stroke.pts,
          over: true,
        }),
      );
    },
    [sendBoardData],
  );

  const clear = useCallback(() => {
    setStrokes([]);
    sendBoardData(encodeBoardMsg({ t: 'clear', author: me }));
  }, [me, sendBoardData]);

  return { strokes, add, clear, on, setOn };
}

export type ScreenDrawApi = ReturnType<typeof useScreenDraw>;

/** Канва пометок поверх кадра: прозрачная, кадр видно насквозь. */
export function ScreenDrawLayer({
  draw,
  color,
  widthIdx,
}: {
  draw: ScreenDrawApi;
  /** Цвет и толщина берутся у панели рисования: инструмент в звонке один. */
  color: string;
  widthIdx: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<Pt[] | null>(null);
  const me = useCall().me;

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const paint = () => {
      const W = c.width;
      const H = c.height;
      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const all = live.current
        ? [...draw.strokes, { nonce: '', author: me, color, width: BOARD_WIDTHS[widthIdx].value, pts: live.current }]
        : draw.strokes;
      for (const s of all) {
        ctx.strokeStyle = s.color;
        ctx.lineWidth = Math.max(1, s.width * W);
        ctx.beginPath();
        s.pts.forEach((p, i) => (i ? ctx.lineTo(p.x * W, p.y * H) : ctx.moveTo(p.x * W, p.y * H)));
        if (s.pts.length === 1) ctx.lineTo(s.pts[0].x * W + 0.01, s.pts[0].y * H);
        ctx.stroke();
      }
    };
    paint();
    const ro = new ResizeObserver(() => {
      const r = c.getBoundingClientRect();
      c.width = Math.round(r.width);
      c.height = Math.round(r.height);
      paint();
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, [color, draw.strokes, me, widthIdx]);

  const repaint = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx || !live.current) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, BOARD_WIDTHS[widthIdx].value * c.width);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    live.current.forEach((p, i) => (i ? ctx.lineTo(p.x * c.width, p.y * c.height) : ctx.moveTo(p.x * c.width, p.y * c.height)));
    ctx.stroke();
  };

  // Точка в долях кадра: пометка должна лечь туда же у всех, как бы ни
  // отличался размер окна.
  const at = (e: ReactPointerEvent<HTMLCanvasElement>): Pt => {
    const r = e.currentTarget.getBoundingClientRect();
    const clamp = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };

  if (!draw.on) return null;

  return (
    <canvas
      ref={canvasRef}
      className="screen-draw"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        live.current = [at(e)];
        repaint();
      }}
      onPointerMove={(e) => {
        if (!live.current) return;
        live.current.push(at(e));
        repaint();
      }}
      onPointerUp={() => {
        const pts = live.current;
        live.current = null;
        if (!pts || !pts.length) return;
        // Выравнивание в фигуру работает и здесь: инструмент в звонке один, и
        // вести себя он должен одинаково на листе и поверх экрана.
        const shape = recognize(pts);
        draw.add({
          nonce: newNonce(),
          author: me,
          color,
          width: BOARD_WIDTHS[widthIdx].value,
          pts: shape ? shapePoints(shape) : pts,
        });
      }}
      onPointerCancel={() => {
        live.current = null;
      }}
    />
  );
}
